import { createSelector } from 'reselect';

import { LoadingStateType, STATE_INITIAL, STATE_LOADING, STATE_SUCCESS } from 'Store/commonActions';
import { getCurrentUser } from 'Store/profile/selectors';
import type { User } from 'Store/profile/user.types';

import {
  IdentityVerificationStatus,
  SELFIE_CHALLENGE_REQUIRED,
  SELFIE_CHALLENGE_SUBMITTED,
  SELFIE_VERIFICATION_ANALYTICS_STATUSES_BY_SELFIE_VERIFICATION_STATUS,
  SELFIE_VERIFICATION_REVIEW,
  SELFIE_VERIFICATION_UNVERIFIED,
  SELFIE_VERIFICATION_VERIFIED,
  SelfieVerificationAnalyticsStatus,
} from 'Containers/ModalManager/modals/SelfieVerification/constants';
import isSelfieChallengeRequired from 'Containers/SelfieChallenge/helpers/isSelfieChallengeRequired';

const SELFIE_VERIFICATION_STATUS_MAP: Record<SelfieVerificationAnalyticsStatus, LoadingStateType> =
  {
    [SELFIE_CHALLENGE_REQUIRED]: STATE_INITIAL,
    [SELFIE_VERIFICATION_UNVERIFIED]: STATE_INITIAL,
    [SELFIE_CHALLENGE_SUBMITTED]: STATE_LOADING,
    [SELFIE_VERIFICATION_REVIEW]: STATE_LOADING,
    [SELFIE_VERIFICATION_VERIFIED]: STATE_SUCCESS,
  };

/**
 * getSelfieVerificationStatus gets the user's selfie verification status
 * @param  { GlobalStateType } state - top-level state
 * @return { IdentityVerificationStatus } selfieVerificationStatus
 */
export const getSelfieVerificationStatus = createSelector(
  getCurrentUser,
  (user: User): IdentityVerificationStatus =>
    user?.selfie_verification || SELFIE_VERIFICATION_UNVERIFIED
);

/**
 * getSelfieVerificationAnalyticsStatus gets the user's selfie verification
 * status and maps it to the associated analytics status
 * @param  { GlobalStateType } state - top-level state
 * @return { IdentityVerificationStatus } selfieVerificationAnalyticsStatus
 */
export const getSelfieVerificationAnalyticsStatus = createSelector(
  getSelfieVerificationStatus,
  (status) => SELFIE_VERIFICATION_ANALYTICS_STATUSES_BY_SELFIE_VERIFICATION_STATUS[status]
);

/**
 * getSelfieVerificationMappedStatus gets the user's selfie verification status
 * and maps it to a common action status
 * @param  { GlobalStateType } state - top-level state
 * @return { Number } mappedSelfieVerificationStatus
 */
export const getSelfieVerificationMappedStatus = createSelector(
  getSelfieVerificationStatus,
  (status) => SELFIE_VERIFICATION_STATUS_MAP[status] ?? STATE_INITIAL
);

/**
 * getIsSelfieChallengeRequired determines whether the user should be selfie challenged
 * or has a challenge submission pending.
 * @param  { GlobalStateType } state - top-level state
 * @return { boolean } isSelfieChallenged
 */
export const getIsSelfieChallengeRequired = createSelector(
  getSelfieVerificationStatus,
  isSelfieChallengeRequired
);

/**
 * getIsSelfieChallengeSuccessful determines if the selfie challenge was successfully completed
 * @param { GlobalStateType } state - top-level state
 * @returns { boolean } isSelfieChallengeSuccessful
 */
export const getIsSelfieChallengeSuccessful = createSelector(
  getSelfieVerificationStatus,
  (status) => status === SELFIE_VERIFICATION_VERIFIED
);

/**
 * getIsSelfieChallengeFailed determines if the selfie challenge was failed
 * @param { GlobalStateType } state - top-level state
 * @returns { boolean } isSelfieChallengeFailed
 */
export const getIsSelfieChallengeFailed = createSelector(
  getSelfieVerificationStatus,
  (status) => status === SELFIE_VERIFICATION_UNVERIFIED
);
